import type { Metric } from "@unbox/api";
import type React from "react";
import { useRef, useState } from "react";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import {
  MetricsConfigurationForm,
  type MetricsConfigurationFormHandle,
  type MetricsFormData,
} from "./MetricsConfigurationForm";

interface MetricsConfigurationModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: MetricsFormData) => Promise<unknown> | unknown;
  initialData?: MetricsFormData;
  isEditMode?: boolean;
  isSubmitting?: boolean;
  autoCloseOnSubmit?: boolean;
  generateSuggestedName?: (params: {
    layout: "chart" | "table" | "bar";
    chartMetrics: (Metric | "")[];
    tableMetrics: (Metric | "")[];
  }) => string;
  copy: {
    createTitle: string;
    editTitle: string;
    nameLabel: string;
    detailsSectionLabel?: string;
    detailsSectionSubtitle?: string;
    submitLabels: {
      create: string;
      edit: string;
      creating?: string;
      updating?: string;
    };
  };
  /** Optional filtered metrics array (e.g., to hide Ads metrics when user hasn't authed) */
  filteredMetrics?: Array<{ value: string; label: string; description: string }>;
}

export const MetricsConfigurationModal: React.FC<MetricsConfigurationModalProps> = ({
  isOpen,
  onOpenChange,
  onSubmit,
  initialData,
  isEditMode = false,
  isSubmitting = false,
  autoCloseOnSubmit = true,
  generateSuggestedName,
  copy,
  filteredMetrics,
}) => {
  const formRef = useRef<MetricsConfigurationFormHandle>(null);
  const [lastOpenState, setLastOpenState] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  // Track modal open state for form reset behavior
  const modalJustOpened = isOpen && !lastOpenState;
  if (isOpen !== lastOpenState) {
    setLastOpenState(isOpen);
  }

  const handleSubmit = async (data: MetricsFormData) => {
    const result = await onSubmit(data);
    if (autoCloseOnSubmit && result !== false && result !== null) {
      onOpenChange(false);
    }
    return result;
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formRef.current) {
      await formRef.current.submit();
    }
  };

  const submitButtonText = () => {
    if (isEditMode) {
      return isSubmitting ? (copy.submitLabels.updating ?? copy.submitLabels.edit) : copy.submitLabels.edit;
    }
    return isSubmitting ? (copy.submitLabels.creating ?? copy.submitLabels.create) : copy.submitLabels.create;
  };

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange} className="sm:items-start sm:pt-20">
      <Modal className="w-2xl">
        <Dialog id="Modal" className="bg-white rounded-lg shadow-none p-6 w-full">
          <form onSubmit={handleFormSubmit} className="w-full">
            <div className="flex flex-row items-center justify-between space-y-0 pb-4">
              <h2 className="text-lg font-semibold">{isEditMode ? copy.editTitle : copy.createTitle}</h2>
              <span>
                <CloseButton onClick={handleCancel} />
              </span>
            </div>

            <MetricsConfigurationForm
              ref={formRef}
              onSubmit={handleSubmit}
              initialData={modalJustOpened ? initialData : undefined}
              isEditMode={isEditMode}
              isSubmitting={isSubmitting}
              autoCloseOnSubmit={autoCloseOnSubmit}
              generateSuggestedName={generateSuggestedName}
              nameLabel={copy.nameLabel}
              detailsSectionLabel={copy.detailsSectionLabel}
              detailsSectionSubtitle={copy.detailsSectionSubtitle}
              resetOnInitialDataChange={true}
              onValidityChange={setIsFormValid}
              filteredMetrics={filteredMetrics}
            />

            <div className="mt-6 flex gap-2 justify-end">
              <Button type="button" color="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit" isDisabled={!isFormValid || isSubmitting} color="primary">
                {submitButtonText()}
              </Button>
            </div>
          </form>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};

// Re-export types for convenience
export type { MetricsFormData };
